import { useEffect, useId, useRef, useState } from "react";
import type { AnalyticsDrilldownRow } from "../../hooks/useAnalytics";
import { exportReport, type ReportExportContext } from "../../lib/analytics-reports";
import { Button } from "../ui/Button";

export function ReportExportMenu({ rows, context, disabled }: { rows: AnalyticsDrilldownRow[]; context: ReportExportContext; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const id = useId();
  const container = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const close = () => { setOpen(false); container.current?.querySelector("button")?.focus(); };
  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector("button")?.focus();
    const outside = (event: PointerEvent) => { if (!container.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);
  async function output(format: "csv" | "json" | "copy") {
    const result = exportReport(context, rows);
    try {
      if (format === "copy") { await navigator.clipboard.writeText(JSON.stringify(result.payload, null, 2)); setNotice("Report context copied."); }
      else {
        const blob = new Blob([format === "csv" ? result.csv : JSON.stringify(result.payload, null, 2)], { type: format === "csv" ? "text/csv;charset=utf-8" : "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a"); link.href = url; link.download = `analytics-${context.report.replace(/[^a-z0-9-]/gi, "-")}-${context.start}.${format}`;
        document.body.appendChild(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setNotice(`Exported ${rows.length} matching rows.`);
      }
      close();
    } catch { setNotice("Export failed. Your report and filters are unchanged."); }
  }
  return <div ref={container} className="relative basis-full sm:basis-auto" onKeyDown={event => { if (event.key === "Escape" && open) { event.preventDefault(); close(); } }}>
    <Button variant="secondary" size="sm" disabled={disabled} aria-haspopup="dialog" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>Export ▾</Button>
    {open && <div ref={menu} id={id} role="dialog" aria-label="Export report" className="absolute left-0 top-full z-20 mt-2 flex w-56 flex-col items-stretch rounded-lg border border-slate-200 bg-white p-2 shadow-lg sm:left-auto sm:right-0">
      <Button variant="ghost" size="sm" onClick={() => void output("csv")}>Download CSV</Button>
      <Button variant="ghost" size="sm" onClick={() => void output("json")}>Download JSON</Button>
      <Button variant="ghost" size="sm" onClick={() => void output("copy")}>Copy context</Button>
      <p className="px-3 py-2 text-xs text-slate-500">All {rows.length} matching returned rows, with report coverage and filters.</p>
    </div>}
    {notice && <p role="status" className="mt-1 text-xs text-slate-600">{notice}</p>}
  </div>;
}
